/**
 * The owner's private progress page (REQ-OPS-021, D54).
 *
 * Served only on progress.lens-specs.com, behind Cloudflare Access. It is NOT
 * in `knownRoute`, `staticPage`, the sitemap or any link: the public build
 * contains it, but the public API answers 404 to /api/v1/progress, so on the
 * public host it can only ever say "not available". Nothing here is a number
 * written by hand; every figure comes from the API on load.
 */
import { useEffect, useState } from "preact/hooks";

interface Day { date: string; accepted_values: number; new_lenses: number }
interface Progress {
  totals: { lenses: number; accepted_values: number; sources: number; pending_values: number };
  growth: {
    days: Day[];
    today: { accepted_values: number; new_lenses: number };
    last_7_days: { accepted_values: number; new_lenses: number };
  };
  coverage_by_attribute: {
    key: string; label: string; prominence: number;
    applicable: number; covered: number; percent: number | null;
  }[];
  coverage_by_maker: {
    slug: string; name: string; lenses: number;
    p1_applicable: number; p1_gaps: number; p1_percent: number | null;
  }[];
  computed_at: string;
}

/** `/progress` or `/progress/`. Deliberately separate from `knownRoute`. */
export function isProgressPath(path: string): boolean {
  return /^\/progress\/?$/.test(path);
}

const n = (x: number) => x.toLocaleString("en-US");
const pct = (x: number | null) => (x === null ? "n/a" : `${x}%`);

export function ProgressPage() {
  const [data, setData] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Progress";
    fetch("/api/v1/progress", { headers: { accept: "application/json" } })
      .then((r) => {
        if (r.status === 404) throw new Error("There is nothing at this address.");
        if (!r.ok) throw new Error("Couldn't load the progress report.");
        return r.json() as Promise<Progress>;
      })
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, []);

  return (
    <div class="shell">
      <main class="page progress-page">
        <h1 class="page-title">Progress</h1>
        {error && <p role="alert">{error}</p>}
        {!data && !error && <p>Loading…</p>}
        {data && <Report p={data} />}
      </main>
    </div>
  );
}

function Report({ p }: { p: Progress }) {
  const maxVals = Math.max(1, ...p.growth.days.map((d) => d.accepted_values));
  return (
    <>
      <p class="page-lede">Computed {new Date(p.computed_at).toLocaleString()}</p>

      <section class="page-section" aria-labelledby="pg-totals">
        <h2 id="pg-totals">Totals</h2>
        <dl class="cell-states">
          <dt>{n(p.totals.lenses)}</dt><dd>lenses</dd>
          <dt>{n(p.totals.accepted_values)}</dt><dd>accepted values</dd>
          <dt>{n(p.totals.sources)}</dt><dd>sources</dd>
          <dt>{n(p.totals.pending_values)}</dt><dd>values waiting for review</dd>
        </dl>
      </section>

      <section class="page-section" aria-labelledby="pg-growth">
        <h2 id="pg-growth">Growth</h2>
        <p>
          Today: {n(p.growth.today.accepted_values)} accepted values, {n(p.growth.today.new_lenses)} new lenses.
          Last 7 days: {n(p.growth.last_7_days.accepted_values)} accepted values,{" "}
          {n(p.growth.last_7_days.new_lenses)} new lenses.
        </p>
        <div class="table-scroll">
          <table class="rule-table">
            <thead>
              <tr><th>Day</th><th>Accepted values</th><th></th><th>New lenses</th></tr>
            </thead>
            <tbody>
              {[...p.growth.days].reverse().map((d) => (
                <tr key={d.date}>
                  <td>{d.date}</td>
                  <td>{n(d.accepted_values)}</td>
                  <td aria-hidden="true">
                    <span class="progress-bar" style={{ width: `${(100 * d.accepted_values) / maxVals}%` }} />
                  </td>
                  <td>{n(d.new_lenses)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section class="page-section" aria-labelledby="pg-attr">
        <h2 id="pg-attr">Coverage by attribute</h2>
        <p>Lenses with an accepted value, out of the lenses it applies to (a cited not-applicable takes the lens out). P1 first.</p>
        <div class="table-scroll">
          <table class="rule-table">
            <thead>
              <tr><th>Attribute</th><th>Priority</th><th>Covered</th><th>Applicable</th><th>%</th></tr>
            </thead>
            <tbody>
              {p.coverage_by_attribute.map((a) => (
                <tr key={a.key}>
                  <td>{a.label}</td>
                  <td>P{a.prominence}</td>
                  <td>{n(a.covered)}</td>
                  <td>{n(a.applicable)}</td>
                  <td>{pct(a.percent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section class="page-section" aria-labelledby="pg-maker">
        <h2 id="pg-maker">Coverage by maker</h2>
        <p>Largest P1 gap first.</p>
        <div class="table-scroll">
          <table class="rule-table">
            <thead>
              <tr><th>Maker</th><th>Lenses</th><th>P1 gaps</th><th>P1 cells</th><th>P1 covered</th></tr>
            </thead>
            <tbody>
              {p.coverage_by_maker.map((m) => (
                <tr key={m.slug}>
                  <td>{m.name}</td>
                  <td>{n(m.lenses)}</td>
                  <td>{n(m.p1_gaps)}</td>
                  <td>{n(m.p1_applicable)}</td>
                  <td>{pct(m.p1_percent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
